import * as Sentry from '@sentry/browser';
import pSettle from 'p-settle';

import Api from './api/ApiClient';
import SessionStorage from './api/SessionStorage';

import doLoginFlow from './login';

// This is the main entry point for mugge.
// Here we want to start everything that we might need

// First step is to initialize the error reporting, but only for prod
if (process.env.NODE_ENV !== 'development') {
  Sentry
    .init({
      dsn: 'https://6c455ce706c14e52ad12a7f3801e308e@sentry.procode.it/2',
      /* global __COMMIT_HASH__:false */
      release: __COMMIT_HASH__ || 'unreleased'
    });
}

// So first step is to test, whether we can query our own data and the configuration
async function bootstrap() {
  let configuration = null;
  let me = null;

  const configPromise = Api.get('/configuration');

  if (SessionStorage.get()) {
    const [ resConfig, resMe ] = await pSettle([
      configPromise,
      Api.get('/auth/me')
    ]);

    if (resConfig.isRejected) {
      throw resConfig.reason;
    } else {
      configuration = resConfig.value;
    }

    if (resMe.isFulfilled) {
      me = resMe.value;
    } else {
      SessionStorage.unset();
      const err = resMe.reason;

      console.error('Failed to obtain initial configuration', err);
      Sentry.captureException(err);
    }
  } else {
    configuration = await configPromise;
  }

  const initialData = {
    configuration
  };

  let isFirstLogin = false;
  if (!me) {
    // Not logged in, make sure we start the login flow
    const { sessionId, firstLogin } = await doLoginFlow();

    SessionStorage.set(sessionId);
    isFirstLogin = firstLogin;

    initialData.me = await Api.get('/auth/me');
  } else {
    initialData.me = me;
  }

  const { createAppStore, startApp } = await import('../src/app');

  if (process.env.NODE_ENV !== 'production') {
    // So these here below are development shortcuts
    const devKey = '__dev_perm_mode';

    let permMode = localStorage.getItem('__dev_perm_mode', devKey);

    try {
      permMode = JSON.parse(permMode);
    } catch (ex) {
      console.warn(ex);
    }

    if (typeof permMode === 'object' && permMode !== null) {
      if (!initialData.me) {
        throw new Error('Dev config but without logged in user');
      }

      // So either organizer, admin, musician
      const { type, ...rest } = permMode;

      if (type === 'musician' && !rest.group) {
        throw new Error('Invalid dev config');
      }

      if (type === 'organizer' && !rest.forGroup) {
        throw new Error('Invalid dev config');
      }

      const override = {
        _key: initialData.me._key
      };

      if (type === 'admin') {
        override.permissions = { ...me.permissions, administration: true };
        override.group = null;
      } else if (type === 'organizer') {
        override.permissions = { 'a': 1, [rest.forGroup]: 2 };
        override.group = rest.forGroup;
      } else if (type === 'musician') {
        override.permissions = { [rest.group]: 1 };
        override.group = rest.group;
      } else {
        throw new Error('Invalid dev config');
      }

      Object.assign(initialData.me, override);
      window.__dev_local_user_override = override;
    }

    Object.assign(window, {
      devAsAdmin() {
        localStorage.setItem(devKey, JSON.stringify({ type: 'admin' }));
        window.location.reload();
      },

      devAsOrganizer(forGroup) {
        if (!configuration.groups[forGroup]) {
          throw new Error('Invalid group');
        }

        localStorage.setItem(devKey, JSON.stringify({ type: 'organizer', forGroup }));
        window.location.reload();
      },

      devAsMusician(group) {
        if (!configuration.groups[group]) {
          throw new Error('Invalid group');
        }

        localStorage.setItem(devKey, JSON.stringify({ type: 'musician', group }));
        window.location.reload();
      },

      devReset() {
        localStorage.removeItem(devKey);
        window.location.reload();
      }
    });
  }

  const store = createAppStore(initialData);
  startApp(store, { isFirstLogin });
}

bootstrap()
  .catch(ex => {
    console.error(ex);
    Sentry.captureException(ex);

    throw ex;
  });